'use client';

import { ChartDataPoint } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface SavingsTableProps {
  chartData: ChartDataPoint[];
  translations: ToolsTranslations['savings'];
  currency: string;
}

export default function SavingsTable({ chartData, translations, currency }: SavingsTableProps) {
  // Get locale based on currency
  const getLocale = (currency: string) => {
    switch (currency) {
      case '£': return 'en-GB';
      case '$': return 'en-US';
      case '€': return 'de-DE';
      case 'CHF': return 'de-CH';
      case 'kr': 
        // For 'kr' we need to determine if it's SEK, NOK, or DKK based on context
        // Since we can't distinguish between them by symbol alone, default to Swedish
        return 'sv-SE';
      default: return 'sv-SE';
    }
  };

  const locale = getLocale(currency);

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20 max-w-7xl mx-auto">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">{translations.chartTitle}</h3>
        <p className="text-sm text-gray-600">{translations.chartSubtitle}</p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b-2 border-gray-200">
              <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider" style={{ textAlign: 'center' }}>
                {translations.savingsPeriod}
              </th>
              <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider" style={{ textAlign: 'center' }}>
                {translations.chartData.savings}
              </th>
              <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider" style={{ textAlign: 'center' }}>
                {translations.chartData.return}
              </th>
              <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider" style={{ textAlign: 'center' }}>
                {translations.chartData.total}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {chartData.map((dataPoint, index) => (
              <tr 
                key={index} 
                className="hover:bg-gray-50 transition-colors"
              >
                <td className="px-6 py-4 text-sm font-medium text-gray-900 text-center">
                  {dataPoint.year}
                </td>
                <td className="px-6 py-4 text-sm text-center text-emerald-700 font-medium">
                  {Math.round(dataPoint['Sparande']).toLocaleString(locale)} {currency}
                </td>
                <td className="px-6 py-4 text-sm text-center text-green-700 font-medium">
                  {Math.round(dataPoint['Avkastning']).toLocaleString(locale)} {currency}
                </td>
                <td className="px-6 py-4 text-sm text-center text-blue-700 font-semibold">
                  {Math.round(dataPoint['Totalt']).toLocaleString(locale)} {currency}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

